import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
const faqs = [{
  q: '¿Necesito contarte algo antes de la sesión?',
  a: 'Sí — antes de cada sesión te pido que me compartas tu intención: qué es lo que querés trabajar. No necesitás explicarme tu historia ni tener todo claro. Solo lo que sentís que necesita ser atendido. Esa intención guía el trabajo desde el primer momento y permite que la sesión sea considerablemente más precisa y profunda.'
}, {
  q: '¿Cómo funciona a distancia?',
  a: 'El campo energético no está limitado por el espacio físico. El Método Llave opera en el plano vibracional, que es independiente de la distancia. No necesitás estar presente para que el trabajo llegue a tu campo.'
}, {
  q: '¿Necesito creer en esto para que funcione?',
  a: 'No. El trabajo se realiza sobre tu campo energético, no sobre tus creencias. Tu intención de recibirlo es suficiente.'
}, {
  q: '¿Los bloqueos siempre vienen de afuera?',
  a: 'No, y sería deshonesto decir que sí. Los bloqueos pueden tener múltiples orígenes. Lo que encuentro en tu campo te lo cuento en el audio — después de que el trabajo ocurrió.'
}, {
  q: '¿Por qué no me contás más antes de la sesión?',
  a: 'Porque hay información que cobra un sentido completamente diferente cuando llega después de que el trabajo ocurrió. El audio está diseñado para ser recibido así.'
}, {
  q: '¿Cuándo recibo mi audio?',
  a: 'A la tarde del día de tu sesión.'
}, {
  q: '¿Qué servicio elijo si no sé por dónde empezar?',
  a: 'La Depuración Energética Esencial es siempre el punto de entrada natural.'
}, {
  q: '¿Cómo agendo mi turno?',
  a: 'Me escribís directamente y coordinamos la fecha según disponibilidad. El turno queda confirmado una vez realizado el pago — que debe acreditarse antes de las 24 horas previas a tu sesión.'
}, {
  q: '¿Puedo pedirte que trabajes algo específico?',
  a: 'Sí — de hecho, te lo voy a pedir yo. Tu intención es parte del proceso. Podés compartirme lo que sentís que necesita ser atendido y eso guiará el trabajo desde el primer momento.'
}, {
  q: '¿Qué pasa si necesito cancelar?',
  a: 'Más de 48hs antes: reembolso completo o crédito para reagendar. Entre 48 y 24hs: el monto queda como crédito para una futura sesión. Menos de 24hs: no se realizan reembolsos. Si por alguna razón excepcional necesito cancelar de mi parte, te ofrezco reembolso completo o prioridad absoluta en el reagendado.'
}, {
  q: '¿Hacés sesiones para niños?',
  a: 'Consultame directamente — cada situación es particular y prefiero hablarlo antes de confirmar.'
}, {
  q: '¿Se puede hacer la sesión para otra persona sin que lo sepa?',
  a: 'No. El trabajo sobre el campo energético de una persona es un acto íntimo que requiere su consentimiento. No trabajo sobre campos de terceros sin que ellos lo hayan pedido y acordado. Y hay una razón que va más allá de la ética: el éxito de una sesión depende en un 50% de las ganas genuinas de mejorar de quien la recibe. El campo responde al trabajo — pero la integración, el movimiento real en la vida cotidiana, requiere que la persona esté dispuesta a recibirlo. No se puede querer sanar en nombre de otro. Cada uno transita su proceso cuando está listo. Si hay alguien en tu vida que creés que podría beneficiarse de este trabajo — la mejor forma de ayudarlo es compartirle esta página y dejar que decida.'
}, {
  q: '¿Después de la sesión tengo que hacer algo?',
  a: 'Depende de lo que la sesión revele — y te lo voy a contar en el audio. Lo que sí puedo decirte es esto: la sesión limpia el camino. Despeja lo que estaba obstruyendo. Pero lo que ocurra después depende de vos. La información que recibís en el audio es un punto de partida — una invitación a un cambio que solo vos podés hacer. Si esa información se integra, se trabaja, se usa como brújula — el campo se mantiene más limpio y liviano. Si se ignora, es probable que con el tiempo las energías negativas vuelvan a formarse. No porque el trabajo no haya funcionado — sino porque el campo energético es dinámico. Responde a lo que hacemos, pensamos y sentimos. La sesión te da la llave. Lo que abrís con ella — eso es tuyo.'
}, {
  q: '¿Tengo que hacer algo mientras me hacen la sesión?',
  a: 'No es necesario que hagas nada en especial. Podés hacer tu vida normalmente ese día — trabajar, descansar, salir, lo que sea. El trabajo se realiza en el plano energético y no requiere que estés en ningún estado particular ni que sepas exactamente cuándo está ocurriendo. Tu campo está disponible independientemente de lo que estés haciendo. Lo único que importa es la intención que me compartiste antes — el resto corre por mi cuenta.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleOpen = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="w-full bg-cream py-24 px-6" data-id="element-76">
      <div className="max-w-3xl mx-auto" data-id="element-77">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="font-serif text-teal text-3xl md:text-4xl lg:text-5xl text-center mb-16" data-id="element-78">
          Preguntas frecuentes
        </motion.h2>

        <div className="space-y-4" data-id="element-79">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.05
        }} className="bg-white rounded-xl overflow-hidden border border-teal/10 shadow-sm" data-id="element-80">
              <button onClick={() => toggleOpen(index)} className="w-full text-left px-6 py-5 flex items-center justify-between focus:outline-none" data-id="element-81">
                <span className="font-serif text-teal-dark text-lg md:text-xl pr-8" data-id="element-82">
                  {faq.q}
                </span>
                <ChevronDown className={`text-teal transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} size={24} data-id="element-83" />
              </button>

              <AnimatePresence data-id="element-84">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-85">
                    <div className="px-6 pb-6 text-gray-600 font-light leading-relaxed border-t border-teal/5 pt-4" data-id="element-86">
                      {faq.a}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}